import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Wrench, MessageSquare, User, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { activityService } from '../../services/activityService';
import { useDateFormat } from '../../hooks/useDateFormat';

const CATEGORY_ICONS = {
  CMMS:    <Wrench className="w-5 h-5" />,
  CHATBOT: <MessageSquare className="w-5 h-5" />,
  AUTH:    <User className="w-5 h-5" />,
};

const CATEGORY_COLORS = {
  CMMS:    'bg-blue-100 text-blue-600',
  CHATBOT: 'bg-purple-100 text-purple-600',
  AUTH:    'bg-orange-100 text-orange-600',
};

const DATE_ONLY_KEYS = [
  'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
];

// Keeps the translated relative-time strings this page has always used; the shared
// helper falls back to English when no labels are supplied.
const RELATIVE_LABELS = (t) => ({
  justNow:    ()      => t('activityLog.justNow'),
  minutesAgo: (count) => t('activityLog.minutesAgo', { count }),
  hoursAgo:   (count) => t('activityLog.hoursAgo', { count }),
  daysAgo:    (count) => t('activityLog.daysAgo', { count }),
});

const ActivityLog = () => {
  const { t } = useTranslation();
  const { formatRelativeTime } = useDateFormat();
  const companyId = localStorage.getItem('companyId');

  const [logs, setLogs]             = useState([]);
  const [stats, setStats]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [page, setPage]             = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [filters, setFilters]       = useState({ category: '', search: '', dateFrom: '', dateTo: '' });

  const SIZE = 20;

  const fetchLogs = useCallback(async (pageNum = 0) => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const data = await activityService.getCompanyActivity(companyId, {
        page: pageNum, size: SIZE, ...filters
      });
      setLogs(data.content || []);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);
      setPage(pageNum);
    } catch {
      setError(t('activityLog.loadError'));
    } finally {
      setLoading(false);
    }
  }, [companyId, filters]);

  const fetchStats = useCallback(async () => {
    if (!companyId) return;
    try {
      const data = await activityService.getCompanyStats(companyId);
      setStats(data);
    } catch {
      // non-critical
    }
  }, [companyId]);

  useEffect(() => { fetchLogs(0); }, [filters]);
  useEffect(() => { fetchStats(); }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleDateKeyDown = (e) => {
    if (!DATE_ONLY_KEYS.includes(e.key) && !/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const statsCards = stats ? [
    { label: t('activityLog.today'),       value: stats.totalToday },
    { label: t('activityLog.thisWeek'),    value: stats.thisWeek },
    { label: t('activityLog.thisMonth'),   value: stats.thisMonth },
    { label: t('activityLog.activeUsers'), value: stats.activeUsersToday },
  ] : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('activityLog.title')}</h1>
          <p className="text-gray-600 mt-1">{t('activityLog.subtitle')}</p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid md:grid-cols-4 gap-4">
          {statsCards.map((s) => (
            <div key={s.label} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-2xl font-bold text-gray-900">{s.value?.toLocaleString() ?? '—'}</div>
              <div className="text-sm text-gray-600">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('activityLog.searchPlaceholder')}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            />
          </div>
          <select
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">{t('activityLog.allActivities')}</option>
            <option value="CMMS">CMMS</option>
            <option value="CHATBOT">Chatbot</option>
            <option value="AUTH">{t('activityLog.account')}</option>
          </select>
          <input
            type="date"
            value={filters.dateFrom ? filters.dateFrom.split('T')[0] : ''}
            onChange={(e) => handleFilterChange('dateFrom', e.target.value ? e.target.value + 'T00:00:00' : '')}
            onKeyDown={handleDateKeyDown}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <input
            type="date"
            value={filters.dateTo ? filters.dateTo.split('T')[0] : ''}
            onChange={(e) => handleFilterChange('dateTo', e.target.value ? e.target.value + 'T23:59:59' : '')}
            onKeyDown={handleDateKeyDown}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
      </div>

      {/* Log List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6">{t('activityLog.recentActivity')}</h2>

        {error && <div className="text-red-600 text-sm mb-4">{error}</div>}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-gray-500">{t('activityLog.noActivity')}</div>
        ) : (
          <div className="space-y-2">
            {logs.map((log) => (
              <div key={log.id} className="flex items-start gap-4 p-4 rounded-lg hover:bg-gray-50 transition-colors">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${CATEGORY_COLORS[log.category] || 'bg-gray-100 text-gray-600'}`}>
                  {CATEGORY_ICONS[log.category] || <Activity className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{log.action}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {log.actorName && <span className="text-xs text-gray-500">{log.actorName}</span>}
                    {log.actorName && <span className="text-xs text-gray-400">•</span>}
                    <span className="text-xs text-gray-500">{formatRelativeTime(log.createdAt, RELATIVE_LABELS(t))}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              {t('activityLog.totalActivities', { count: totalElements.toLocaleString() })}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => fetchLogs(page - 1)}
                disabled={page === 0}
                className="px-3 py-1 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-40 text-sm"
              >
                {t('common.previous')}
              </button>
              <span className="px-3 py-1 text-sm text-gray-600">
                {t('activityLog.pageOf', { current: page + 1, total: totalPages })}
              </span>
              <button
                onClick={() => fetchLogs(page + 1)}
                disabled={page >= totalPages - 1}
                className="px-3 py-1 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-40 text-sm"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLog;
